import Image from '@tiptap/extension-image'

/**
 * Ảnh kéo góc đổi kích thước được, mở rộng từ `@tiptap/extension-image` đã cài.
 *
 * ─── VÌ SAO TỰ VIẾT THAY VÌ THÊM THƯ VIỆN ─────────────────────────────────────────
 * Tiptap không có gói resize chính thức, các gói cộng đồng đều kéo theo React node view
 * và một bộ CSS riêng. Phần thật sự cần chỉ là hai thứ: thêm 2 attribute và một node
 * view DOM thuần — gọn hơn nhiều so với một dependency nữa (CLAUDE.md §7).
 *
 * ─── VÌ SAO LÀ THUỘC TÍNH width/height CHỨ KHÔNG PHẢI style ───────────────────────
 * `apps/api/src/lib/sanitize.ts` cho phép sẵn `img: ['src','alt','width','height']`. Đi
 * bằng `style="width:480px"` là phải nới allowlist cho `style` trên `img` — mở rộng bề
 * mặt mà không được gì thêm. Nhờ vậy phần resize này KHÔNG cần đụng tới sanitizer.
 *
 * ─── VÌ SAO ẢNH LÀ INLINE ─────────────────────────────────────────────────────────
 * `inline: true` đặt ảnh nằm trong đoạn văn, nên **căn lề ảnh có sẵn và miễn phí** từ
 * `text-align` của đoạn chứa nó — đúng cách WooCommerce/WordPress làm. Nếu để ảnh là
 * block thì `text-align` đặt lên chính thẻ `<img>` không có tác dụng gì, và phải mở
 * thêm `style` trên `img` cho sanitizer chỉ để căn giữa được một tấm ảnh.
 */

const MIN_WIDTH = 48

/** Đọc số dương từ attribute HTML; mọi thứ khác coi như không có. */
function toPositiveInt(value: unknown): number | null {
  const n = Number.parseInt(String(value ?? ''), 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => toPositiveInt(el.getAttribute('width')),
        renderHTML: (attrs) => (attrs.width ? { width: String(attrs.width) } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => toPositiveInt(el.getAttribute('height')),
        renderHTML: (attrs) => (attrs.height ? { height: String(attrs.height) } : {}),
      },
    }
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      // Bọc trong <span>, không phải <div>: node là inline, một block lồng trong <p> sẽ
      // làm ProseMirror và trình duyệt bất đồng về cây DOM.
      const wrapper = document.createElement('span')
      wrapper.className = 'rt-img'

      const img = document.createElement('img')
      const label = document.createElement('span')
      label.className = 'rt-img__label'
      wrapper.append(img, label)

      /** Tỉ lệ khung hình, ưu tiên attribute đã lưu, sau đó mới tới kích thước thật của file. */
      let aspect = 1
      const readAspect = (w: number | null, h: number | null) => {
        if (w && h) aspect = w / h
        else if (img.naturalWidth && img.naturalHeight) aspect = img.naturalWidth / img.naturalHeight
      }

      const apply = (attrs: Record<string, unknown>) => {
        img.src = String(attrs.src ?? '')
        img.alt = String(attrs.alt ?? '')
        const w = toPositiveInt(attrs.width)
        const h = toPositiveInt(attrs.height)
        if (w) img.setAttribute('width', String(w))
        else img.removeAttribute('width')
        if (h) img.setAttribute('height', String(h))
        else img.removeAttribute('height')
        readAspect(w, h)
      }
      apply(node.attrs)
      img.addEventListener('load', () => readAspect(toPositiveInt(img.getAttribute('width')), toPositiveInt(img.getAttribute('height'))), { once: true })

      // ── 4 nút kéo ở góc ─────────────────────────────────────────────────────────
      // Trái/phải khác nhau ở dấu: kéo mép trái sang trái là TO ra, nên dx phải đổi dấu.
      const CORNERS: [string, number][] = [['nw', -1], ['ne', 1], ['sw', -1], ['se', 1]]
      for (const [corner, dir] of CORNERS) {
        const handle = document.createElement('span')
        handle.className = `rt-img__handle rt-img__handle--${corner}`
        handle.addEventListener('pointerdown', (event) => {
          event.preventDefault()
          event.stopPropagation()
          const startX = event.clientX
          const startWidth = img.getBoundingClientRect().width
          // Trần = bề rộng vùng soạn thảo. Ảnh rộng hơn khung thì `max-width:100%` sẽ
          // co lại khi hiển thị, và con số lưu trong DB thành lời nói dối.
          const maxWidth = editor.view.dom.clientWidth
          let width = Math.round(startWidth)

          const onMove = (moveEvent: PointerEvent) => {
            width = Math.round(
              Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + (moveEvent.clientX - startX) * dir)),
            )
            // Chỉ đổi DOM khi đang kéo. Ghi vào document mỗi pointermove sẽ đẻ ra hàng
            // trăm bước undo cho một lần kéo.
            img.setAttribute('width', String(width))
            img.setAttribute('height', String(Math.round(width / aspect)))
            label.textContent = `${width} px`
            wrapper.classList.add('rt-img--resizing')
          }
          const onUp = () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            wrapper.classList.remove('rt-img--resizing')
            const pos = typeof getPos === 'function' ? getPos() : null
            if (typeof pos !== 'number') return
            const height = Math.round(width / aspect)
            editor.view.dispatch(
              editor.view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, width, height }),
            )
          }
          window.addEventListener('pointermove', onMove)
          window.addEventListener('pointerup', onUp)
        })
        wrapper.append(handle)
      }

      return {
        dom: wrapper,
        // Không có contentDOM: ảnh là node lá, ProseMirror không được ghi gì vào trong.
        update(updatedNode) {
          if (updatedNode.type.name !== node.type.name) return false
          apply(updatedNode.attrs)
          return true
        },
        selectNode() { wrapper.classList.add('rt-img--selected') },
        deselectNode() { wrapper.classList.remove('rt-img--selected') },
        ignoreMutation: () => true,
      }
    }
  },
})
